import { SerializedData, Store } from "bernie-core";
import { action, computed, observable } from "mobx";
import { Hotel } from "typings/microserviceModels/hotels-flex-module";
import {
  DxHotel,
  StoredDxHotel
} from "components/flexComponents/DestinationWhereToStay/typings.d.ts";
import { Logger, NOOP_LOGGER } from "bernie-logger";
import { BexApiService } from "src/services/BexApiService";
import { getBexApiContext } from "bernie-context";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import {
  DxPropertySearchQuery,
  MapPropertySearchById,
  MapPropertySearchByLatLong,
  MessageType,
  PropertyDateRangeInput,
  PropertySearchFiltersInput,
  PropertySearchQuery
} from "__generated__/api";
import { isVariantEnabled, Experiment } from "src/components/utility/experiment";
import { getNextFriday } from "src/components/utility/DateUtils";
import { PropertyFilterType } from "src/model/PropertyFilter";
import {
  constructBexApiPropertyFilter,
  isValidBexApiFilterOption,
  filterOptionHasValidRange,
  convertClassOptionValue
} from "src/components/utility/PropertyFilterUtils";
import { BexApiMessage } from "model/BexApiMessage";
import Messages = MapPropertySearchByLatLong.Messages;
import Query = MapPropertySearchByLatLong.Query;
import { Filter, PropertyForMap, FilterOption } from "uitk-react-experimental-module-map";
import { formatText } from "bernie-l10n";

export class HotelsStore extends Store {
  @observable
  public allHotelsOnMap: PropertyForMap[] = [];

  @observable
  public storedWhereToStayHotels: StoredDxHotel = [];

  @observable
  public filters: Filter[];

  @observable
  public bexApiMessages: BexApiMessage[] = [];

  @observable
  public isLoading: boolean = false;

  public constructor(
    state: SerializedData = {},
    logger: Logger = NOOP_LOGGER,
    private bexApiService: BexApiService
  ) {
    super(state, logger);
    this.bexApiService = bexApiService;
    this.filters = [];
  }

  /**
   * Get the images of each hotel from Query.PropertySearch
   * @param hotels - List of hotels of which we can use their ids to find their
   * respective galleries
   */
  @action
  public async fetchGallery(hotels: Hotel[], contextStore: ExtendedContextStore) {
    if (!hotels || hotels.length === 0) {
      return {};
    }

    const hotelIds: string[] = hotels.map(hotel => hotel.hotelId);

    const variables: PropertySearchQuery.Variables = {
      context: getBexApiContext(contextStore),
      hotelIds
    };

    try {
      const query = await this.bexApiService.fetchPropertySearchQuery(variables);

      const galleries = {};
      query.data.propertySearch.properties.forEach(property => {
        if (!property.gallery) {
          return;
        }

        galleries[property.id] = property.gallery.map(img => ({
          src: img.url,
          alt: img.description
        }));
      });

      return galleries;
    } catch (err) {
      return {};
    }
  }

  public setStoredWhereToStayHotels = (neighborhoodId: string, hotels: DxHotel[]) => {
    this.storedWhereToStayHotels = {
      ...this.storedWhereToStayHotels,
      [neighborhoodId]: hotels
    };
  };

  /**
   * Returns a function
   */
  private getLabel(
    defaultLocKey: string,
    optionsSelectedLocKey: string
  ): (totalOptionsSelected: number) => string {
    return (totalOptionsSelected: number) => {
      return totalOptionsSelected <= 0
        ? formatText(defaultLocKey)
        : formatText(optionsSelectedLocKey, totalOptionsSelected);
    };
  }

  /**
   * Returns a correctly structured list of filter options based on the Module
   * Map's needed
   */
  private filterOption({
    filterOptions,
    label,
    type,
    extraFilter
  }: {
    filterOptions: MapPropertySearchById._Options[];
    label: (totalOptionsSelected: number) => string;
    type: PropertyFilterType;
    extraFilter?: (opt: MapPropertySearchById._Options) => boolean;
  }): Filter | null {
    const options: FilterOption[] = [];

    for (const opt of filterOptions) {
      if (!opt.label || !isValidBexApiFilterOption(opt)) {
        continue;
      }
      if (extraFilter && !extraFilter(opt)) {
        continue;
      }

      let value: string | number = opt.value ? opt.value : "";
      if (type === PropertyFilterType.CLASS && opt.value) {
        value = convertClassOptionValue(opt.value);
      }

      options.push({
        label: opt.label,
        value,
        range: opt.range ? opt.range : undefined,
        enumValue: opt.enumValue ? opt.enumValue : undefined
      });
    }

    if (options.length === 0) {
      return null;
    }

    return {
      label,
      type,
      options
    };
  }

  @action
  public async fetchHotels(hotelIds: string[], contextStore: ExtendedContextStore): Promise<any> {
    if (!hotelIds.length) {
      return [];
    }

    const variables: DxPropertySearchQuery.Variables = {
      context: getBexApiContext(contextStore),
      hotelIds
    };

    try {
      let hotels: DxHotel[] = [];

      const query = await this.bexApiService.fetchDxPropertySearchQuery(variables);
      const hotelsFromResponse = query.data.propertySearch.properties;

      /* istanbul ignore next line */
      for (const hotel of hotelsFromResponse) {
        if (hotels.length === 4) {
          break;
        }

        const { id, name, gallery, infositeURL, reviews, price } = hotel;
        if (!(id && name && gallery && infositeURL && reviews && price)) {
          continue;
        }

        const newHotel: DxHotel = {
          id,
          name,
          hotelSearchLink: infositeURL,
          price: price.lead ? price.lead.formatted : "",
          rating: reviews.score,
          reviews: reviews.total,
          hotelImage: gallery[0].url,
          priceValue: price.lead ? price.lead.amount : 0
        };

        hotels = [...hotels, newHotel];
      }

      return hotels;
    } catch (err) {
      return [];
    }
  }

  @action
  public async fetchMapHotelsById(hotels: Hotel[], contextStore: ExtendedContextStore) {
    this.isLoading = true;
    const isFilteringEnabled = isVariantEnabled(
      contextStore,
      Experiment.Flex_HTG_Blossom_Map_Filtering,
      1
    );

    const searchDateRange: PropertyDateRangeInput = this.getSearchDateRange();
    const checkInDate = `${searchDateRange.checkInDate.year}-${searchDateRange.checkInDate.month}-${
      searchDateRange.checkInDate.day
    }`;

    const checkOutDate = `${searchDateRange.checkOutDate.year}-${
      searchDateRange.checkOutDate.month
    }-${searchDateRange.checkOutDate.day}`;

    const mapCompositionHotelsToMap = () => {
      this.allHotelsOnMap.push(
        ...hotels.map(h => this.mapFlexHotelToPropertyForMap(h, contextStore))
      );
    };

    const variables: MapPropertySearchById.Variables = {
      context: getBexApiContext(contextStore),
      propertyIds: hotels.map(hotel => hotel.hotelId),
      dateRange: searchDateRange
    };

    try {
      const query = await this.bexApiService.fetchMapHotelsById(variables);
      const newHotels: PropertyForMap[] = [];

      // Form and add filters
      if (isFilteringEnabled && (!this.filters || this.filters.length === 0)) {
        const options = query.data.propertySearch.sortAndFilter.options!.sortAndFilter;
        // TODO: Make into enum
        // const supportedFiltersName: string[] = ["price", "star", "lodging"];

        for (const bexApiFilter of options) {
          const filterOptions = bexApiFilter.options;
          const name = bexApiFilter.name;

          switch (name) {
            case PropertyFilterType.PRICE: {
              const parsedFilter: Filter | null = this.filterOption({
                filterOptions,
                type: PropertyFilterType.PRICE,
                label: this.getLabel(
                  "maps.filter.pricePerNight",
                  "maps.filter.pricePerNight.selected"
                ),
                extraFilter: filterOptionHasValidRange
              });

              if (!parsedFilter) {
                break;
              }

              this.filters.push(parsedFilter);
              break;
            }
            case "star": {
              const starFilter: Filter | null = this.filterOption({
                filterOptions,
                type: PropertyFilterType.CLASS,
                label: this.getLabel(
                  "maps.filter.propertyClass",
                  "maps.filter.propertyClass.selected"
                )
              });

              if (!starFilter) {
                break;
              }

              starFilter.optionsCTATypes = "BUTTON";

              this.filters.push(starFilter);
              break;
            }
            case PropertyFilterType.LODGING: {
              const lodgingFilter: Filter | null = this.filterOption({
                filterOptions,
                type: PropertyFilterType.LODGING,
                label: this.getLabel(
                  "maps.filter.propertyType",
                  "maps.filter.propertyType.selected"
                ),
                extraFilter: opt => {
                  return opt.enumValue !== null && typeof opt.enumValue !== "undefined";
                }
              });

              if (!lodgingFilter) {
                break;
              }

              this.filters.push(lodgingFilter);
              break;
            }
            default:
              break;
          }
        }
      }

      for (const property of query.data.propertySearch.properties) {
        // Check if the property has all the necessary info to be displayed on the map
        if (!property.mapMarker || !property.mapMarker.latLong || !property.infositeURL) {
          continue;
        }

        const formattedPrice =
          property.price && property.price.lead && property.price.lead.formatted;

        const priceAmount = property.price && property.price.lead && property.price.lead.amount;

        const newHotel: PropertyForMap = {
          id: property.id,
          propertyName: property.name,
          formattedPrice: priceAmount ? formattedPrice : "",
          latitude: property.mapMarker.latLong.latitude,
          longitude: property.mapMarker.latLong.longitude,
          overallReviewRating: formatText(
            "maps.sheets.hotel.reviewRating.text",
            property.reviews.score
          ),
          reviewCount: formatText("maps.sheets.hotel.reviews.text", property.reviews.total),
          imageUrl: property.image && property.image.url,
          hrefUrl: this.constructPinnedHSRUrl(
            contextStore.searchContext.location.id.toString(),
            property.id,
            checkInDate,
            checkOutDate
          ),
          icon: {
            type: priceAmount ? "price" : "soldOut"
          }
        };

        newHotels.push(newHotel);
      }

      this.allHotelsOnMap = newHotels;
    } catch (err) {
      mapCompositionHotelsToMap();
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Fetch an array of hotels for a given latitude and longitude. Queries the
   * BEX API and reassigns the store's allHotelsOnMaps and isLoading property
   * and returns it.
   * @param {ExtendedContextStore} contextStore - Context to be passed to getBextApiContext
   * @param {number} lat - Latitude on the map that the user is looking at
   * @param {number} lng - Longitude on the map that the user is looking at
   * @returns {Promise<PropertyForMap[]>} - Array of found hotels. Defaults to empty array in case of errors
   * @memberof HotelsStore
   */
  @action
  public async fetchHotelsByLatLong(
    contextStore: ExtendedContextStore,
    lat: number,
    lng: number
  ): Promise<void> {
    // Used for loading indicators
    this.isLoading = true;
    // Get search date range to determine pricing of hotels near the user
    const searchDateRange: PropertyDateRangeInput = this.getSearchDateRange();
    // Format check in and check out date to be what PropertyForMap expects
    const checkInDate = `${searchDateRange.checkInDate.year}-${searchDateRange.checkInDate.month}-${
      searchDateRange.checkInDate.day
    }`;
    const checkOutDate = `${searchDateRange.checkOutDate.year}-${
      searchDateRange.checkOutDate.month
    }-${searchDateRange.checkOutDate.day}`;
    // Get BexAPI context, filters and prep rest of variables
    const variables: MapPropertySearchByLatLong.Variables = {
      context: getBexApiContext(contextStore),
      latitude: lat,
      longitude: lng,
      dateRange: searchDateRange,
      filters: this.constructBexApiFilter()
    };

    try {
      const newHotels: PropertyForMap[] = [];
      const query = await this.bexApiService.fetchMapHotelsForLatLong(variables);
      this.populateBexApiMessages(query.data);
      // Check if the hotel/property has all the necessary info to be displayed on the map
      for (const property of query.data.propertySearch.properties) {
        if (property.mapMarker && property.mapMarker.latLong && property.infositeURL) {
          const newHotel: PropertyForMap = {
            id: property.id,
            propertyName: property.name,
            formattedPrice:
              property.price && property.price.lead ? property.price.lead.formatted : undefined,
            latitude: property.mapMarker.latLong.latitude,
            longitude: property.mapMarker.latLong.longitude,
            overallReviewRating: property.reviews.score,
            reviewCount: property.reviews.total,
            imageUrl: property.image && property.image.url,
            hrefUrl: this.constructPinnedHSRUrl(
              contextStore.searchContext.location.id.toString(),
              property.id,
              checkInDate,
              checkOutDate
            ),
            icon: {
              type: "price"
            }
          };

          newHotels.push(newHotel);
        }
      }

      // Replace existing properties
      this.allHotelsOnMap = newHotels;
    } catch (err) {
      // when the call fails, clear previous messages. Error component defaults to default message.
      this.bexApiMessages = [];
      throw err;
    } finally {
      // Done loading
      this.isLoading = false;
    }
  }

  /**
   * Checks whether our list of bex api messages contains a message about overfiltering.
   * @return: Overfiltering BexApiMessage or undefined when such message is not available.
   */
  public getOverfilteredMessage(): BexApiMessage | undefined {
    return this.bexApiMessages.find(message => message.type === MessageType.OVERFILTERED);
  }
  /**
   * Getter for allHotelsOnMap. Filters through them to prevent the bug on BEX
   * API/our data where we sometimes have hotels on in the middle of the ocean
   * (0,0).
   * @readonly
   * @type {PropertyForMap[]}
   */
  @computed
  public get hotelsOnMap(): PropertyForMap[] {
    return this.allHotelsOnMap.filter(
      (hotel: PropertyForMap) => hotel.latitude !== 0 || hotel.longitude !== 0
    );
  }

  private hasEnabledFilterOptions(filters: Filter[]): boolean {
    for (const filter of filters) {
      for (const opt of filter.options) {
        if (opt.isSelected === true) {
          return true;
        }
      }
    }

    return false;
  }

  public constructBexApiFilter(): PropertySearchFiltersInput {
    const filter: PropertySearchFiltersInput = {};
    filter.showAvailableOnly = true;

    if (this.hasEnabledFilterOptions(this.filters)) {
      constructBexApiPropertyFilter(filter, this.filters);
    }

    return filter;
  }

  /**
   * This method converts a hotel coming from the flex composition into a hotel that can be placed on the map.
   * @param hotel A hotel coming from the composition.
   * @return a hotel for map
   */
  public mapFlexHotelToPropertyForMap(
    hotel: Hotel,
    contextStore: ExtendedContextStore
  ): PropertyForMap {
    const searchDateRange: PropertyDateRangeInput = this.getSearchDateRange();
    const checkInDate = `${searchDateRange.checkInDate.year}-${searchDateRange.checkInDate.month}-${
      searchDateRange.checkInDate.day
    }`;

    const checkOutDate = `${searchDateRange.checkOutDate.year}-${
      searchDateRange.checkOutDate.month
    }-${searchDateRange.checkOutDate.day}`;

    const language = contextStore.locale.split(/[_-]/);

    let overallReviewRating;
    if (hotel.hotelOverallReviewRating) {
      overallReviewRating = formatText(
        "maps.sheets.hotel.reviewRating.text",
        hotel.hotelOverallReviewRating.toLocaleString(language, { maximumFractionDigits: 1 })
      );
    }

    let reviewCount;
    if (hotel.reviewCount) {
      reviewCount = formatText(
        "maps.sheets.hotel.reviews.text",
        hotel.reviewCount.toLocaleString(language, { maximumFractionDigits: 1 })
      );
    }

    return {
      id: hotel.hotelId,
      propertyName: hotel.hotelName,
      formattedPrice: hotel.formattedPrice,
      latitude: hotel.hotelLatitude,
      longitude: hotel.hotelLongitude,
      overallReviewRating,
      reviewCount,
      imageUrl: hotel.hotelImageUrl,
      hrefUrl: this.constructPinnedHSRUrl(
        contextStore.searchContext.location.id.toString(),
        hotel.hotelId,
        checkInDate,
        checkOutDate
      ),
      icon: {
        type: hotel.formattedPrice ? "price" : "soldOut"
      }
    };
  }

  public hydrate(data: SerializedData): void {
    // The serialized store as returned by the server exposes a stripped down
    // version of bexApiService with no methods.We delete it here to prevent the override.
    delete data.bexApiService;

    Object.assign(this, data);
  }

  /**
   * Calculate and return search date range for hotels we want to find around
   * our user. We choose check-in on a Friday two weeks from now. Note that the
   * range is not based on what the user input in the Wizard, so it's not dynamic
   * @returns {PropertyDateRangeInput} - Check in date and check out dates
   */
  public getSearchDateRange(): PropertyDateRangeInput {
    //Check-in is Friday two weeks from now
    //Check out is the day after that
    const today = new Date();
    const checkInDate = getNextFriday(today);
    const checkOutDate = new Date(checkInDate);
    checkOutDate.setDate(checkInDate.getDate() + 1);

    return {
      checkInDate: {
        day: checkInDate.getDate(),
        month: checkInDate.getMonth() + 1,
        year: checkInDate.getFullYear()
      },
      checkOutDate: {
        day: checkOutDate.getDate(),
        month: checkOutDate.getMonth() + 1,
        year: checkOutDate.getFullYear()
      }
    };
  }

  public constructPinnedHSRUrl(
    regionId: string,
    hotelId: string,
    startDate: string,
    endDate: string
  ) {
    return `/Hotel-Search?selected=${hotelId}&PinnedHotelID=${hotelId}&HadPinnedHotel=true&regionId=${regionId}&startDate=${startDate}&endDate=${endDate}`;
  }

  private populateBexApiMessages(query: Query) {
    this.bexApiMessages = query.propertySearch.summary.messages
      ? query.propertySearch.summary
          .messages!.filter(message => validateBexApiMessage(message))
          .map(message => {
            const subtitle: string | null =
              (message!.subtitle && message!.subtitle!.text && message!.subtitle!.text!) || null;

            return { type: message.type!, title: message.title!.text!, subtitle };
          })
      : [];
  }
}

function validateBexApiMessage(message: Messages): boolean {
  return (
    message.title !== undefined &&
    message.title !== null &&
    message.title.text !== undefined &&
    message.title.text !== null &&
    message.type !== undefined &&
    message.type !== null &&
    message.subtitle !== undefined
  );
}
